import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import axios from 'axios';
import VueAxios from 'vue-axios';

const cloneDeep = require('lodash/cloneDeep');

function arrayMove(array, oldIndex, newIndex) {
	while (oldIndex < 0) {
		oldIndex += array.length;
	}
	while (newIndex < 0) {
		newIndex += array.length;
	}
	if (newIndex >= array.length) {
		let k = newIndex - array.length + 1;
		while (k--) {
			array.push(undefined);
		}
	}
	array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
}

function findEntity(entities, id, parents) {
	const nextParent = parents.shift();
	if (nextParent)
		return findEntity(
			entities.find((entity) => entity.id === nextParent),
			id,
			parents
		);
	return entities.find((entity) => entity.id === id);
}

function getPanelTemplate() {
	return {
		expandedIndex: null,
		tools: [],
	};
}

Vue.use(Vuex);
Vue.use(VueAxios, axios);

const vuexLocalStorage = new VuexPersist({
	key: 'vuex',
	storage: window.localStorage,
	reducer: (state) => ({
		user: state.user,
		config: state.config,
		authToken: state.authToken,
	}),
});

export default new Vuex.Store({
	state: {
		config: null,
		user: null,
		authToken: null,
		project: null,
		scenario: null,
		activityLog: { status: 'loading', data: '' },
		errorMessages: '',
		bg_class: 'bg-water',
		datasetClipboard: null,
		tourClipboard: null,
		projectListIsOpen: true,
		entities: [],
		tags: [],
		openPanels: {
			projectTools: {
				slim: getPanelTemplate(),
				wide: getPanelTemplate(),
			},
			visualizationTools: {
				slim1: getPanelTemplate(),
				slim2: getPanelTemplate(),
			},
		},
		currentTourStop: null,
		tours: [],
		activeTourIndex: -1,
		createTourStopIsOpen: false,
		createTourStopMode: 'createButtons',
		projectList: [],
		enterpriseList: [],
		tourViewProperties: {},
		elementEditorEntityDataId: null,
		osmSelect: false,
		buildingsSelected: false,
		isLoading: false,
		isRendering: false,
	},
	getters: {
		activeTour(state) {
			return state.tours[state.activeTourIndex];
		},
		getActivityLog: (state) => state.activityLog.data,
		getProjectTools: (state) => state.projectTools,
		getErrorMessages: (state) => state.errorMessages,
		getSmartPanel: (state) => state.smartPanel,
		getConfig: (state) => state.config,
		getAuthToken: (state) => state.authToken,
		getProject: (state) => state.project,
		getScenario: (state) => state.scenario,
		getBaseUrl: (state) => {
			const baseUrlStripped = state.config.baseurl.replace(/\/+$/, '');
			return `${baseUrlStripped}/${state.config.apiVersion}/`;
		},
		getDatasetClipboard: (state) => state.datasetClipboard,
		getTourClipboard: (state) => state.tourClipboard,
		getUser: (state) => state.user,
		getCurrentTourStop: (state) => state.currentTourStop,
		getProjectList: (state) => state.projectList,
		getEnterpriseList: (state) => state.enterpriseList,
		getTourViewProperties: (state) => state.tourViewProperties,
		getElementEditorEntityDataId: (state) => state.elementEditorEntityDataId,
	},
	mutations: {
		setLoading(state, input) {
			state.isLoading = input;
		},
		setRendering(state, input) {
			state.isRendering = input;
		},
		moveEntity(state, { id, index, parents }) {
			const entities = cloneDeep(state.entities);
			const parent = findEntity(entities, parents.pop(), parents);
			const entityArray = parent ? parent.children : entities;
			arrayMove(
				entityArray,
				entityArray.findIndex((entity) => entity.id === id),
				index
			);
			state.entities = entities;
		},
		setConfig(state, config) {
			state.config = config;
		},
		setUser(state, user) {
			state.user = user;
		},
		setProject(state, project) {
			state.project = project;
		},
		setScenario(state, scenario) {
			state.scenario = scenario;
		},
		setAuthToken(state, token) {
			state.authToken = token;
		},
		setDatasetClipboard(state, dataset) {
			state.datasetClipboard = dataset;
		},
		setTourClipboard(state, value) {
			state.tourClipboard = value;
		},
		setProjectListIsOpen(state, isOpen) {
			state.projectListIsOpen = isOpen;
		},
		setElementEditorEntityDataId(state, entityId) {
			state.elementEditorEntityDataId = entityId;
		},
		setEntities(state, entities) {
			const [roots, tags] = entities;
			state.entities = roots;
			state.tags = tags;
		},
		setTags(state, tags) {
			state.tags = tags;
		},
		setOsmSelect(state, enterOsmSelectMode) {
			state.osmSelect = enterOsmSelectMode;
		},
		setBuildingsSelected(state, buildingsSelected) {
			state.buildingsSelected = buildingsSelected;
		},
		showHideAllTags(state) {
			const allAreVisible = state.tags.every((tag) => tag.visible);
			for (const tag of state.tags) tag.visible = !allAreVisible;
		},
		toggleTagVisibility(state, name) {
			const tagToChange = state.tags.find((tag) => tag.name === name);
			tagToChange.visible = !tagToChange.visible;
		},
		openPanel(state, { area, panel, smartPanel }) {
			let isPanelAbsent = true;
			for (const panels of state.openPanels[area][smartPanel].tools) {
				if (panels.componentName === panel.componentName) isPanelAbsent = false;
			}
			if (isPanelAbsent) state.openPanels[area][smartPanel].tools.push(panel);
		},
		expandPanel(state, { area, index, smartPanel }) {
			if (state.openPanels[area][smartPanel].expandedIndex === null) {
				state.openPanels[area][smartPanel].expandedIndex = index;
			} else {
				state.openPanels[area][smartPanel].expandedIndex = null;
			}
		},
		closePanel(state, { area, index, smartPanel }) {
			const panelsCopy = cloneDeep(state.openPanels);
			panelsCopy[area][smartPanel].expandedIndex = null;
			panelsCopy[area][smartPanel].tools.splice(index, 1);
			state.openPanels = panelsCopy;
		},
		closeAllPanels(state) {
			state.openPanels = {
				projectTools: {
					slim: getPanelTemplate(),
					wide: getPanelTemplate(),
				},
				visualizationTools: {
					slim1: getPanelTemplate(),
					slim2: getPanelTemplate(),
				},
			};
		},
		setCurrentTourStop(state, currentTourStop) {
			state.currentTourStop = currentTourStop;
		},
		setTours(state, tours) {
			state.tours = tours;
		},
		addTour(state, tour) {
			state.activeTourIndex = state.tours.length;
			tour.data.stops = [];
			tour.data.activeStopIndex = -1;
			state.tours.push(tour);
			state.createTourStopIsOpen = true;
			state.createTourStopMode = 'createButtons';
		},
		setCreateTourStopIsOpen(state, isOpen) {
			state.createTourStopIsOpen = isOpen;
		},
		addActiveTourStop(state, stop) {
			stop.modified = DateTime.now();
			stop.id = uuidv4();
			if (
				!(typeof state.tours[state.activeTourIndex] === 'object' && !!state.tours[state.activeTourIndex].data)
			) {
				state.tours[state.activeTourIndex].data = {};
			}
			if (state.tours[state.activeTourIndex].data && !state.tours[state.activeTourIndex].data.stops) {
				state.tours[state.activeTourIndex].data.stops = [];
			}

			state.tours[state.activeTourIndex].data.stops.push(stop);
			state.tours[state.activeTourIndex].data.activeStopIndex =
				state.tours[state.activeTourIndex].data.stops.length - 1;
		},
		setActiveTourStop(state, index) {
			state.tours[state.activeTourIndex].data.activeStopIndex = index;
		},
		setActiveTour(state, index) {
			state.activeTourIndex = index;
		},
		moveActiveTourStop(state, { oldIndex, newIndex }) {
			arrayMove(state.tours[state.activeTourIndex].data.stops, oldIndex, newIndex);
		},
		deleteTourStop(state, index) {
			if (state.tours[state.activeTourIndex].data.activeStopIndex === index) {
				state.tours[state.activeTourIndex].data.activeStopIndex = -1;
			}
			state.tours[state.activeTourIndex].data.stops.splice(index, 1);
		},
		setCreateTourStopMode(state, mode) {
			state.createTourStopMode = mode;
		},
		setProjectList(state, projectList) {
			state.projectList = projectList;
		},
		setEnterpriseList(state, enterpriseList) {
			state.enterpriseList = enterpriseList;
		},
		setTourViewProperties(state, properties) {
			state.tourViewProperties = properties;
		},
		updateTourViewProperties(state, properties) {
			Object.assign(state.tourViewProperties, properties);
		},
	},
	actions: {
		async setError({ commit }, err) {
			await commit('SET_ERROR_MESSAGE', err);
		},
		async fetchProjectList(context) {
			try {
				return axios.get('/projects').then((response) => {
					if (response.status === 200) {
						context.commit('setProjectList', response.data);
						return true;
					}
					return false;
				});
			} catch (error) {
				console.error(error);
			}
			return false;
		},
		async fetchUserEnterprises(context) {
			try {
				return axios.get('/enterprises').then((response) => {
					if (response.status === 200) {
						context.commit('setEnterpriseList', response.data);
						return true;
					}
					return false;
				});
			} catch (error) {
				console.error(error);
			}
			return false;
		},
		async savePreferredEnterprise(context, enterpriseId) {
			try {
				return axios.patch('/account', { preferredEnterpriseId: enterpriseId }).then((response) => {
					if (response.status === 200) {
						context.commit('setEnterpriseList', response.data.enterprises);
						delete response.data.enterprises;
						context.commit('setUser', response.data);
					}
					return true;
				});
			} catch (error) {
				console.error(error);
			}
			return false;
		},
		async refreshUserToken(context) {
			try {
				return axios.get('/refresh-token').then((response) => {
					if (response.status === 200) {
						context.commit('setAuthToken', response.data.apiToken);
						return true;
					}
					return false;
				});
			} catch (error) {
				console.error(error);
			}
			return false;
		},
		async updateTreeList({ commit }, list) {
			commit('setEntities', list);
		},
	},
	modules: {},
	plugins: [vuexLocalStorage.plugin],
});
